import { useParams, useNavigate } from 'react-router-dom';
import { Icon, Button } from '@/components/atoms';
import { PurchaseOrderDetail } from '@/components/organisms/PurchaseOrder';
import { AttachmentList } from '@/components/organisms/Attachment/AttachmentList';
import { usePurchaseOrderDetail } from '@/hooks/useErpApi';

export default function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: purchaseOrder, isLoading, error, refetch } = usePurchaseOrderDetail(id || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading purchase order...</p>
        </div>
      </div>
    );
  }

  if (error || !purchaseOrder) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="CircleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">
                Failed to Load Purchase Order
              </h3>
              <p className="text-error-700 mb-3">{error?.message || 'Purchase order not found'}</p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => navigate('/purchase-orders')}>
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to List
                </Button>
                <Button variant="primary" size="sm" onClick={() => refetch()}>
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/purchase-orders')}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Back
        </Button>
      </div>

      <PurchaseOrderDetail purchaseOrder={purchaseOrder as never} onUpdate={refetch} />

      {/* Purchase Order Attachments */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <AttachmentList
          entityType="po"
          entityId={purchaseOrder.id}
          allowUpload={true}
          allowDelete={true}
          viewMode="list"
        />
      </div>
    </div>
  );
}
